export interface OnboardingAssignableMember {
  userId: string;
  email?: string | null;
  name?: string | null;
  role?: string | null;
}

export type OnboardingTaskKind = 'brief' | 'escalation';
export type OnboardingEscalationTarget = 'none' | 'sales' | 'delivery_owner' | 'support';
export type OnboardingFallbackMode = 'current_user' | 'first_member';

export interface OnboardingAssignmentPolicy {
  id: string;
  label: string;
  appliesTo: OnboardingTaskKind;
  escalationTarget: OnboardingEscalationTarget | 'any';
  rolePriority: string[];
  fallback: OnboardingFallbackMode;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
}

export const DEFAULT_ONBOARDING_ASSIGNMENT_POLICIES: OnboardingAssignmentPolicy[] = [
  {
    id: 'brief-current-user',
    label: 'Onboarding Brief -> Current User',
    appliesTo: 'brief',
    escalationTarget: 'any',
    rolePriority: [],
    fallback: 'current_user',
    isActive: true,
  },
  {
    id: 'sales-followup-owner',
    label: 'Sales Follow-up -> Manager/Admin/Supervisor',
    appliesTo: 'escalation',
    escalationTarget: 'sales',
    rolePriority: ['manager', 'admin', 'supervisor'],
    fallback: 'current_user',
    isActive: true,
  },
  {
    id: 'delivery-review-owner',
    label: 'Delivery Review -> Admin/Manager/Supervisor',
    appliesTo: 'escalation',
    escalationTarget: 'delivery_owner',
    rolePriority: ['admin', 'manager', 'supervisor'],
    fallback: 'current_user',
    isActive: true,
  },
  {
    id: 'support-review-owner',
    label: 'Support Review -> Supervisor/Manager/Admin',
    appliesTo: 'escalation',
    escalationTarget: 'support',
    rolePriority: ['supervisor', 'manager', 'admin'],
    fallback: 'current_user',
    isActive: true,
  },
  {
    id: 'generic-escalation-owner',
    label: 'Generic Escalation -> Supervisor/Manager/Admin',
    appliesTo: 'escalation',
    escalationTarget: 'any',
    rolePriority: ['supervisor', 'manager', 'admin'],
    fallback: 'current_user',
    isActive: true,
  },
];

function normalizeMemberRole(role: string | null | undefined): string {
  return String(role ?? '').trim().toLowerCase();
}

export function normalizeOnboardingAssignmentPolicies(
  items: OnboardingAssignmentPolicy[] | null | undefined,
): OnboardingAssignmentPolicy[] {
  if (!Array.isArray(items) || items.length === 0) return DEFAULT_ONBOARDING_ASSIGNMENT_POLICIES;

  const overrideMap = new Map(items.map((item) => [item.id, item]));
  return DEFAULT_ONBOARDING_ASSIGNMENT_POLICIES.map((policy) => ({
    ...policy,
    ...(overrideMap.get(policy.id) ?? {}),
  }));
}

export function resolveOnboardingAssignmentPolicy(
  policies: OnboardingAssignmentPolicy[],
  kind: OnboardingTaskKind,
  escalationTarget: string,
): OnboardingAssignmentPolicy {
  const normalized = normalizeOnboardingAssignmentPolicies(policies);
  const policy = (
    normalized.find((item) => item.appliesTo === kind && item.escalationTarget === escalationTarget && item.isActive !== false)
    ?? normalized.find((item) => item.appliesTo === kind && item.escalationTarget === 'any' && item.isActive !== false)
  );
  return policy ?? {
    id: 'fallback-current-user',
    label: 'Fallback -> Current User',
    appliesTo: kind,
    escalationTarget: 'any',
    rolePriority: [],
    fallback: 'current_user',
    isActive: true,
  };
}

export function resolveOnboardingSuggestedAssignee(
  members: OnboardingAssignableMember[],
  currentUserId: string | null | undefined,
  kind: OnboardingTaskKind,
  escalationTarget: string,
  policies: OnboardingAssignmentPolicy[],
): { member: OnboardingAssignableMember | null; policy: OnboardingAssignmentPolicy } {
  const policy = resolveOnboardingAssignmentPolicy(policies, kind, escalationTarget);
  const currentMember = members.find((member) => member.userId === currentUserId) ?? null;

  for (const role of policy.rolePriority) {
    const match = members.find((member) => normalizeMemberRole(member.role) === role);
    if (match) return { member: match, policy };
  }

  if (policy.fallback === 'current_user') {
    return { member: currentMember ?? members[0] ?? null, policy };
  }

  return { member: members[0] ?? currentMember ?? null, policy };
}
